import { Modal } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { ActivateCampaignStepContent, ActivateCampaignStepContentProps } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/activate-campaign-step-content-without-button';
import { useAfterCampaignStepsSelectOptions } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/hooks/use-after-campaign-steps-select-options';
import { Action, ActionType, State, reducer } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/store';
import { transformActivateDataForBackend, transformCampaignStepActivateDataToState } from 'modules/campaigns/campaign-steps/components/activate-campaign-step/utils';
import { CampaignStepActivateData, CampaignStepId } from 'modules/campaigns/campaign-steps/types/campaign-step-interface';
import { CampaignId } from 'modules/campaigns/types/campaign-interface';
import React, { memo, useEffect, useReducer, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { ChangeSenderEmailModal, useChangeSenderEmailModal } from '../change-sender-email-modal';
import { useActivateCampaignStep } from './hooks/use-activate-campaign-step';
import { ActivateCampaignStepErrorResponseContentInterface, ActivateCampaignStepErrors } from './types';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
export type ActivateCampaignStepWithoutButtonProps = Pick<ActivateCampaignStepContentProps, 'withConfirmButton' | 'className' | 'errors'> & ({
  withModal: true;
  isOpened: boolean;
  onClose: () => void;
} | {
  withModal?: false;
  isOpened?: never;
  onClose?: never;
}) & {
  onConfirm?: (activateData: CampaignStepActivateData) => Promise<void>;
  onChange?: (activateData: CampaignStepActivateData) => void;
  initialData: CampaignStepActivateData | null;
  campaignStepId: CampaignStepId;
  campaignId: CampaignId;
  isPreFetching?: boolean;
  senderEmail?: string;
  onSenderEmailChange?: ({
    fromEmail
  }: {
    fromEmail: string;
  }) => void;
};
export const ActivateCampaignStepWithoutButton = memo(function ActivateCampaignStepWithoutButtonWithoutMemo({
  className,
  withModal,
  isOpened,
  onClose,
  withConfirmButton,
  onConfirm,
  onChange,
  initialData,
  campaignStepId,
  isPreFetching,
  campaignId,
  errors: errorsFromProps,
  senderEmail,
  onSenderEmailChange
}: ActivateCampaignStepWithoutButtonProps) {
  const {
    t
  } = useLocoTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const {
    revalidateModules
  } = useRevalidateModules();
  const [state_1, dispatch] = useReducer((state: State, action: Action) => reducer(state, action, state_0 => onChange && onChange(transformActivateDataForBackend(state_0))), transformCampaignStepActivateDataToState(initialData));
  useEffect(() => {
    dispatch({
      type: ActionType.initial,
      payload: transformCampaignStepActivateDataToState(initialData)
    });
  }, [initialData]);
  const {
    chosenTimeDelay,
    chosenWeekdays
  } = state_1;
  const [errors, setErrors] = useState<ActivateCampaignStepErrors | null>(null);
  const {
    afterCampaignStepsNamesSelectOptions,
    mutateAfterCampaignStepsSelectOptions
  } = useAfterCampaignStepsSelectOptions({
    campaignId: campaignId,
    campaignStepId: campaignStepId
  });
  const {
    fetcherActivateCampaignStep
  } = useActivateCampaignStep();
  const changeSender = useChangeSenderEmailModal({
    campaignStepId,
    senderEmail,
    onSuccess: onSenderEmailChange
  });
  const isButtonDisabled = chosenTimeDelay === null || Object.values(chosenWeekdays).every(value => !value);
  const isContentPreFetching = !afterCampaignStepsNamesSelectOptions || isPreFetching;
  const handleConfirm = async () => {
    if (!isButtonDisabled) {
      try {
        setIsFetching(true);
        await changeSender.tryCheckSenderEmailAndOpenModalIfNotValid(senderEmail);
        const activateData: CampaignStepActivateData = transformActivateDataForBackend(state_1);
        await fetcherActivateCampaignStep(campaignStepId, activateData);
        await mutateAfterCampaignStepsSelectOptions();
        onConfirm && (await onConfirm(activateData));
        withModal && onClose();
        await revalidateModules([RequestModuleEnum.CampaignStep], true);
      } catch (e) {
        if (e instanceof BadRequest) {
          // TODO: move logic into utils
          const errors_0 = e.errors as ActivateCampaignStepErrorResponseContentInterface;
          const fields = !Array.isArray(errors_0.fields) ? errors_0.fields : undefined;
          if (fields) {
            setErrors(prev => ({
              ...prev,
              excludedWeekdays: fields?.excludedWeekdays?.join(''),
              timeOfTheDay: fields?.timeOfTheDay?.join(''),
              delayValue: fields?.delayValue?.join(''),
              stepBefore: fields?.stepBefore?.join('')
            }));
          }
        }
      } finally {
        setIsFetching(false);
      }
    }
  };
  const isActivationFetching = isFetching || changeSender.isFetching;
  const content = <ActivateCampaignStepContent afterCampaignStepsNamesSelectOptions={afterCampaignStepsNamesSelectOptions} className={className} dispatch={dispatch} errors={errors || errorsFromProps} handleConfirm={handleConfirm} isActivationFetching={isActivationFetching} isButtonDisabled={isButtonDisabled} isPreFetching={isContentPreFetching} state={state_1} withConfirmButton={withConfirmButton} />;
  return <>
        {withModal ? <Modal isCentered opened={isOpened} onClose={onClose} title={t('global.activate')} isFetching={isActivationFetching}>
            {content}
            <ChangeSenderEmailModal {...changeSender} />
          </Modal> : <>
            {content}
            <ChangeSenderEmailModal {...changeSender} />
          </>}
      </>;
});